/**
 * Safe replacement for SurveyJS 1.9.129 `sanitizeEditableContent`.
 *
 * The vendor implementation restores the caret with Range.setStart after
 * rewriting innerText. Backspace in a contenteditable often leaves only a
 * <br> or a newline; stripping that empties the node list, so
 * `element.childNodes[0]` is undefined and Chrome throws:
 * Failed to execute 'setStart' on 'Range': parameter 1 is not of type 'Node'.
 *
 * @param {HTMLElement | null | undefined} element
 * @returns {void}
 */
export function sanitizeEditableContent(element) {
  if (!element) return;

  const visibleText = readVisibleText(element);
  if (typeof window === 'undefined' || !window.getSelection || !document.createRange) {
    writeVisibleText(element, visibleText.replace(/\n/g, ''));
    return;
  }

  if (!element.childNodes || element.childNodes.length === 0) {
    writeVisibleText(element, visibleText.replace(/\n/g, ''));
    return;
  }

  const selection = document.getSelection();
  if (!selection || selection.rangeCount === 0) {
    writeVisibleText(element, visibleText.replace(/\n/g, ''));
    return;
  }

  let tailLength = 0;
  try {
    const range = selection.getRangeAt(0);
    const endContainer = range && range.endContainer;
    const lastChild = element.lastChild;
    if (isNode(endContainer) && isNode(lastChild)) {
      range.setStart(endContainer, range.endOffset);
      range.setEndAfter(lastChild);
      selection.removeAllRanges();
      selection.addRange(range);
      tailLength = selection.toString().replace(/\n/g, '').length;
    }
  } catch (_error) {
    tailLength = 0;
  }

  const sanitized = readVisibleText(element).replace(/\n/g, '');
  writeVisibleText(element, sanitized);

  const startNode = element.childNodes[0];
  if (!isNode(startNode)) {
    return;
  }

  const maxOffset = startNode.nodeType === Node.TEXT_NODE
    ? (startNode.textContent || '').length
    : startNode.childNodes.length;
  const offset = clamp(sanitized.length - tailLength, 0, maxOffset);

  try {
    const caret = document.createRange();
    caret.setStart(startNode, offset);
    caret.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caret);
  } catch (_error) {
    // Caret restore is best-effort; never crash the form builder overlay.
  }
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRangeSetStartError(error) {
  const message = error && error.message ? String(error.message) : '';
  return /setStart/i.test(message) && /Node/i.test(message);
}

/**
 * @param {object} surveyCore
 * @returns {boolean}
 */
export function patchSurveyCoreSanitize(surveyCore) {
  if (!surveyCore) return false;
  try {
    Object.defineProperty(surveyCore, 'sanitizeEditableContent', {
      configurable: true,
      enumerable: true,
      writable: true,
      value: sanitizeEditableContent,
    });
    return surveyCore.sanitizeEditableContent === sanitizeEditableContent;
  } catch (_error) {
    return false;
  }
}

function readVisibleText(element) {
  if (typeof element.innerText === 'string') return element.innerText;
  if (typeof element.textContent === 'string') return element.textContent;
  return '';
}

function writeVisibleText(element, nextText) {
  if ('innerText' in element) {
    element.innerText = nextText;
    return;
  }
  element.textContent = nextText;
}

function isNode(value) {
  return typeof Node === 'function' ? value instanceof Node : !!value && typeof value === 'object';
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}
